import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ApiError } from "@/lib/apiClient";
import {
  listKontakteOhneNummer,
  setGeschaeftspartnerNummer,
  type KontaktOhneNummer,
} from "@/lib/kontakte";
import { notifyKontakteNummernBadgeRefresh } from "@/lib/navBadges";
import {
  filterKontakteOhneNummer,
  type MissingNumberFilter,
} from "@/pages/home/missingNumbers";

const OVERVIEW_LINK_CLASS =
  "inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10";

function contactHref(item: KontaktOhneNummer): string {
  const section = item.typ === "lieferant" ? "lieferanten" : "kunden";
  return `/kontakte/${section}/${item.id}`;
}

function contactPlace(item: KontaktOhneNummer): string {
  const place = [item.plz, item.ort].filter((part) => part && part.trim()).join(" ");
  return [place, item.email].filter((part) => part && part.trim()).join(" · ");
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function HomeMissingNumbersTile() {
  const [items, setItems] = useState<KontaktOhneNummer[] | null>(null);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<MissingNumberFilter>("alle");
  const [query, setQuery] = useState("");
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setError(null);
    try {
      const data = await listKontakteOhneNummer();
      setItems(data.items);
      setTotal(data.total);
      setTruncated(data.truncated);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Die Kontakte konnten nicht geladen werden."));
      if (!options?.silent) setItems([]);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const visible = useMemo(
    () => filterKontakteOhneNummer(items ?? [], filter, query),
    [items, filter, query],
  );

  const loading = items === null && !error;

  const save = async (item: KontaktOhneNummer) => {
    const nummer = (drafts[item.id] ?? "").trim();
    if (!nummer) {
      setRowErrors((prev) => ({ ...prev, [item.id]: "Bitte eine Nummer eintragen." }));
      return;
    }
    setSavingId(item.id);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
    try {
      await setGeschaeftspartnerNummer(item.id, nummer);
      setItems((current) => (current ?? []).filter((row) => row.id !== item.id));
      setTotal((current) => Math.max(0, current - 1));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      notifyKontakteNummernBadgeRefresh();
      void reload({ silent: true });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [item.id]: errorMessage(err, "Die Nummer konnte nicht gespeichert werden."),
      }));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card
      id="home-billing-missing-numbers"
      className="eh-home-anchor"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Kontakte ohne Nummer
          {!loading && total > 0 ? (
            <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
              {total} offen
            </span>
          ) : null}
        </span>
      }
      actions={
        <Link to="/kontakte" className={OVERVIEW_LINK_CLASS}>
          Zu Kontakten
        </Link>
      }
      bodyClassName="!p-0"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : error && (items?.length ?? 0) === 0 ? (
        <p className="px-4 py-6 text-sm text-status-red">{error}</p>
      ) : total === 0 ? (
        <p className="px-4 py-6 text-sm text-label-secondary">
          Alle Kunden und Lieferanten haben eine Nummer.
        </p>
      ) : (
        <div>
          <div className="flex flex-col gap-3 border-b border-separator px-4 py-3">
            <SegmentedControl
              name="Kontaktart"
              value={filter}
              onChange={setFilter}
              options={[
                { value: "alle", label: "Alle" },
                { value: "kunde", label: "Kunden" },
                { value: "lieferant", label: "Lieferanten" },
              ]}
            />
            <input
              className="eh-input w-full"
              type="search"
              value={query}
              placeholder="Name, Ort oder E-Mail"
              aria-label="Kontakte ohne Nummer suchen"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {visible.length === 0 ? (
            <p className="px-4 py-6 text-sm text-label-secondary">Keine passenden Kontakte.</p>
          ) : (
            <ul className="eh-home-queue eh-home-queue-scroll" role="list">
              {visible.map((item) => {
                const label = item.typ === "lieferant" ? "Lieferantennummer" : "Kundennummer";
                const saving = savingId === item.id;
                return (
                  <li key={item.id} className="eh-home-queue-card">
                    <div className="eh-home-queue-card__row">
                      <span className="eh-home-queue-row__status eh-home-queue-row__status--show">
                        {item.typ === "lieferant" ? "Lieferant" : "Kunde"}
                      </span>
                      <span className="eh-home-queue-row__copy">
                        <Link to={contactHref(item)} className="eh-home-queue-row__title hover:text-accent">
                          {item.firmenname || "Ohne Namen"}
                        </Link>
                        {contactPlace(item) ? (
                          <span className="eh-home-queue-row__meta">{contactPlace(item)}</span>
                        ) : null}
                      </span>
                    </div>
                    <form
                      className="flex flex-wrap items-center gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void save(item);
                      }}
                    >
                      <input
                        className="eh-input h-8 min-w-0 flex-1"
                        value={drafts[item.id] ?? ""}
                        placeholder={label}
                        aria-label={`${label} für ${item.firmenname || "Kontakt"}`}
                        autoComplete="off"
                        disabled={saving}
                        onChange={(event) =>
                          setDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))
                        }
                      />
                      <Button type="submit" size="sm" loading={saving} disabled={saving}>
                        Speichern
                      </Button>
                    </form>
                    {rowErrors[item.id] ? (
                      <p className="text-xs text-status-red">{rowErrors[item.id]}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {truncated ? (
            <p className="border-t border-separator px-4 py-2 text-xs text-label-secondary">
              {total} Kontakte ohne Nummer – es werden die ersten {items?.length ?? 0} angezeigt.
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
